import {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  MultiSelect,
} from '@otp/ui';
import { useMemo } from 'react';
import { useDeviceContext } from 'src/context/device.context';
import { useMostDevices } from 'src/hooks/query/most/use-most-devices';
import { useStores } from 'src/hooks/query/use-stores';
import { MostQuery } from 'src/graphql/generated/graphql';
import { getActionSupportedDeviceTypes } from 'src/components/most/utils/device-types';

type DevicesProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  disabled?: boolean;
  className?: string;
  field: ControllerRenderProps<TFieldValues, TName>;
  filter?: {
    storeIds?: string[];
    action?: Pick<
      MostQuery,
      | 'appliesToBOS'
      | 'appliesToEPR'
      | 'appliesToPOS'
      | 'appliesToKVS'
      | 'appliesToRHS'
      | 'appliesToWST'
    >;
  };
};

export const DevicesField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  disabled = false,
  className,
  field,
  filter: filterProp,
}: DevicesProps<TFieldValues, TName>) => {
  const { action, ...filter } = filterProp || ({} as any);
  const { stores } = useStores();

  if (!filter.storeIds) {
    // eslint-disable-next-line
    filter.storeIds = stores.map((store) => store.id);
  }
  if (action) {
    filter.types = getActionSupportedDeviceTypes(action);
  }

  const { data, loading } = useMostDevices({
    storeIds: filter.storeIds,
    types: filter.types,
  });
  const { isMobile } = useDeviceContext();

  const entityOptions = useMemo(
    () =>
      data?.records?.map((entity) => ({
        value: entity.code as string,
        name: entity.code as string,
      })),
    [data],
  );

  const defaultValue = field.value;

  return (
    <MultiSelect
      key={field.name}
      multiple={true}
      fullWidth={true}
      className={className}
      options={entityOptions || []}
      disabled={loading || disabled}
      {...field}
      defaultValue={defaultValue}
      value={field.value || []}
      ref={undefined}
      allowSelectAll={true}
      useCheckboxOption={true}
      limitTags={isMobile ? 4 : 5}
    />
  );
};
